import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import axios from 'axios';
import { Layers, Loader2, Check, Clock, Users, Tag, Save, X, RefreshCw, Wrench, Search, Filter, Settings, Sun, Sunset, MapPin } from 'lucide-react';
import { useState, useEffect, useMemo } from 'react';

import Layout from '../components/Layout';
import SyncProgressBar from '../components/SyncProgressBar';
import { useLocationContext } from '../context/LocationContext';

const AMC_SECTIONS = [
  { id: 'mechanics', label: 'Mechanics', icon: Wrench },
  { id: 'child-jobs', label: 'Child Jobs', icon: Tag },
  { id: 'schedule', label: 'Daily Schedule', icon: Clock },
];

const STAGE_KEYS = ['collection', 'amc', 'washing', 'delivery'];
const STAGE_LABELS = { collection: 'Collection', amc: 'AMC', washing: 'Washing', delivery: 'Delivery' };
const STAGE_COLORS = {
  collection: { bg: 'bg-pink-50', border: 'border-pink-200', text: 'text-pink-700', dot: 'bg-pink-400' },
  amc: { bg: 'bg-amber-50', border: 'border-amber-200', text: 'text-amber-700', dot: 'bg-amber-400' },
  washing: { bg: 'bg-blue-50', border: 'border-blue-200', text: 'text-blue-700', dot: 'bg-blue-400' },
  delivery: { bg: 'bg-purple-50', border: 'border-purple-200', text: 'text-purple-700', dot: 'bg-purple-400' },
};

export default function ChildJobs() {
  const queryClient = useQueryClient();
  const [saved, setSaved] = useState(false);
  const [activeSection, setActiveSection] = useState('mechanics');
  const [schedule, setSchedule] = useState({ positions: [] });
  const [templates, setTemplates] = useState([]);
  const [selectedMechanicUids, setSelectedMechanicUids] = useState(new Set());
  const [mechanicSearch, setMechanicSearch] = useState('');
  const [mechanicFilter, setMechanicFilter] = useState('all');
  const { selectedLocationId, currentLocation } = useLocationContext();

  // Fetch settings (location-scoped)
  const { data: settings, isLoading } = useQuery({
    queryKey: ['app-settings', selectedLocationId],
    queryFn: async () => {
      const { data } = await axios.get('/api/settings');
      return data;
    },
  });

  // Fetch Zuper categories
  const { data: categoriesData } = useQuery({
    queryKey: ['zuper-categories'],
    queryFn: async () => {
      const { data } = await axios.get('/api/settings/zuper-categories');
      return data;
    },
    staleTime: 10 * 60 * 1000,
  });

  // Fetch Zuper users (for assignment)
  const { data: usersData } = useQuery({
    queryKey: ['zuper-users'],
    queryFn: async () => {
      const { data } = await axios.get('/api/settings/zuper-users');
      return data;
    },
    staleTime: 5 * 60 * 1000,
  });

  const categories = categoriesData?.categories || [];
  const zuperUsers = usersData?.users || [];

  // Sync local state from settings
  useEffect(() => {
    if (settings?.buggySchedule && settings.buggySchedule.positions) {
      setSchedule(settings.buggySchedule);
    }
  }, [settings?.buggySchedule]);

  useEffect(() => {
    if (settings?.childJobTemplates) {
      setTemplates(settings.childJobTemplates);
    }
  }, [settings?.childJobTemplates]);

  // Sync mechanics selection from saved settings
  useEffect(() => {
    if (settings?.mechanics) {
      const uids = new Set(settings.mechanics.map((m) => m.user_uid).filter(Boolean));
      setSelectedMechanicUids(uids);
    }
  }, [settings?.mechanics]);

  // Mechanics derived data
  const designations = useMemo(() => {
    const set = new Set();
    zuperUsers.forEach((u) => { if (u.designation) set.add(u.designation); });
    return Array.from(set).sort();
  }, [zuperUsers]);

  const filteredMechanics = useMemo(() => {
    let list = zuperUsers;
    if (mechanicFilter !== 'all') list = list.filter((u) => u.designation === mechanicFilter);
    if (mechanicSearch.trim()) {
      const q = mechanicSearch.toLowerCase();
      list = list.filter((u) => u.name.toLowerCase().includes(q) || u.email.toLowerCase().includes(q) || (u.emp_code && u.emp_code.toLowerCase().includes(q)));
    }
    return [...list].sort((a, b) => {
      const aS = selectedMechanicUids.has(a.user_uid) ? 0 : 1;
      const bS = selectedMechanicUids.has(b.user_uid) ? 0 : 1;
      return aS - bS;
    });
  }, [zuperUsers, mechanicFilter, mechanicSearch, selectedMechanicUids]);

  const savedMechanicUids = useMemo(() => {
    return new Set((settings?.mechanics || []).map((m) => m.user_uid).filter(Boolean));
  }, [settings?.mechanics]);

  const handleMechanicToggle = (user) => {
    setSelectedMechanicUids((prev) => {
      const next = new Set(prev);
      if (next.has(user.user_uid)) next.delete(user.user_uid);
      else next.add(user.user_uid);
      return next;
    });
  };

  // Sync Zuper data (users + categories) mutation
  const syncZuperMutation = useMutation({
    mutationFn: async () => {
      const { data } = await axios.post('/api/settings/sync-zuper');
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['zuper-users']);
      queryClient.invalidateQueries(['zuper-categories']);
    },
  });

  // Save mutation (mechanics + child jobs + schedule)
  const updateMutation = useMutation({
    mutationFn: async ({ mechanics, buggySchedule, childJobTemplates }) => {
      const { data } = await axios.post('/api/settings', { mechanics, buggySchedule, childJobTemplates });
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['app-settings'] });
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
    },
  });

  // Position time handlers
  const handlePositionTimeChange = (posIndex, stageKey, field, value) => {
    setSchedule(prev => {
      const newPositions = [...prev.positions];
      newPositions[posIndex] = {
        ...newPositions[posIndex],
        [stageKey]: { ...newPositions[posIndex][stageKey], [field]: value },
      };
      return { ...prev, positions: newPositions };
    });
  };

  // Template category handler
  const handleTemplateCategoryChange = (index, categoryUid) => {
    const cat = categories.find(c => c.category_uid === categoryUid);
    setTemplates(prev => prev.map((t, i) =>
      i === index ? { ...t, categoryUid, categoryName: cat?.category_name || '' } : t
    ));
  };

  // Template assigned users handlers
  const handleAddAssignedUser = (templateIndex, userUid) => {
    const user = zuperUsers.find(u => u.user_uid === userUid);
    if (!user) return;
    setTemplates(prev => prev.map((t, i) => {
      if (i !== templateIndex) return t;
      const existing = t.assignedUsers || [];
      if (existing.some(u => u.userUid === userUid)) return t;
      return { ...t, assignedUsers: [...existing, { userUid, name: user.name }] };
    }));
  };

  const handleRemoveAssignedUser = (templateIndex, userUid) => {
    setTemplates(prev => prev.map((t, i) => {
      if (i !== templateIndex) return t;
      return { ...t, assignedUsers: (t.assignedUsers || []).filter(u => u.userUid !== userUid) };
    }));
  };

  const handleSave = () => {
    const selectedMechanics = zuperUsers
      .filter((u) => selectedMechanicUids.has(u.user_uid))
      .map((u) => ({ user_uid: u.user_uid, name: u.name, email: u.email, designation: u.designation }));
    updateMutation.mutate({ mechanics: selectedMechanics, buggySchedule: schedule, childJobTemplates: templates });
  };

  // Track changes
  const hasChanged = useMemo(() => {
    const scheduleChanged = JSON.stringify(schedule) !== JSON.stringify(settings?.buggySchedule || {});
    const templatesChanged = JSON.stringify(templates) !== JSON.stringify(settings?.childJobTemplates || []);
    const mechanicsChanged = (() => {
      if (selectedMechanicUids.size !== savedMechanicUids.size) return true;
      for (const uid of selectedMechanicUids) { if (!savedMechanicUids.has(uid)) return true; }
      return false;
    })();
    return scheduleChanged || templatesChanged || mechanicsChanged;
  }, [schedule, templates, settings, selectedMechanicUids, savedMechanicUids]);

  const morningPositions = (schedule.positions || []).filter(p => p.slot === 'morning');
  const afternoonPositions = (schedule.positions || []).filter(p => p.slot === 'afternoon');

  return (
    <Layout>
      {/* Header */}
      <header className="bg-white border-b border-brand-light/60 p-3 md:p-4 flex justify-between items-center z-10 shadow-sm relative">
        <SyncProgressBar isSyncing={syncZuperMutation.isPending} />
        <div className="flex items-center gap-2 md:gap-3">
          <div className="w-9 h-9 bg-gradient-to-br from-brand-light to-white rounded-xl flex items-center justify-center shadow-sm">
            <Settings className="w-5 h-5 text-brand-primary" />
          </div>
          <div>
            <h1 className="text-base md:text-xl font-bold text-brand-dark tracking-tight">AMC Settings</h1>
            <p className="text-xs text-brand-neutral hidden md:block">Mechanics, child jobs, and daily schedule</p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          {currentLocation && (
            <span className="hidden md:flex items-center gap-1.5 px-2.5 py-1.5 text-[11px] font-medium text-brand-primary bg-brand-light/60 rounded-lg">
              <MapPin className="w-3 h-3" />
              {currentLocation.name}
            </span>
          )}
          {saved && (
            <div className="flex items-center gap-1.5 text-green-600 text-sm">
              <Check className="w-4 h-4" />
              Saved
            </div>
          )}
          <button
            onClick={() => syncZuperMutation.mutate()}
            disabled={syncZuperMutation.isPending}
            className="flex items-center gap-1.5 px-3 py-2 text-xs font-medium text-brand-dark bg-white border border-brand-light rounded-xl hover:bg-brand-light transition-colors shadow-sm disabled:opacity-50"
            title="Refresh users and categories from Zuper"
          >
            <RefreshCw className={`w-4 h-4 ${syncZuperMutation.isPending ? 'animate-spin' : ''}`} />
            <span className="hidden sm:inline">{syncZuperMutation.isPending ? 'Syncing...' : 'Sync Zuper'}</span>
          </button>
        </div>
      </header>

      {/* Content */}
      <main className="flex-1 overflow-hidden flex bg-[#f5f8f6]">
        {/* AMC Settings Sidebar */}
        <nav className="w-52 flex-shrink-0 bg-[#f5f8f6] overflow-y-auto pt-4 px-3 space-y-1 hidden md:block">
          {AMC_SECTIONS.map(sec => {
            const Icon = sec.icon;
            const isActive = activeSection === sec.id;
            return (
              <button key={sec.id} onClick={() => setActiveSection(sec.id)}
                className={`w-full flex items-center gap-2.5 px-3 py-2.5 rounded-xl text-[13px] font-medium transition-all ${
                  isActive ? 'bg-brand-primary/10 text-brand-primary' : 'text-brand-dark/60 hover:bg-brand-light/60 hover:text-brand-primary'
                }`}>
                <Icon className="w-4 h-4 flex-shrink-0" />
                {sec.label}
              </button>
            );
          })}
        </nav>
        {/* Mobile section selector */}
        <div className="md:hidden p-3 bg-white border-b border-brand-light/60 flex gap-1 overflow-x-auto flex-shrink-0">
          {AMC_SECTIONS.map(sec => (
            <button key={sec.id} onClick={() => setActiveSection(sec.id)}
              className={`px-3 py-1.5 rounded-lg text-[11px] font-medium whitespace-nowrap transition-colors ${
                activeSection === sec.id ? 'bg-brand-primary text-white' : 'bg-gray-100 text-gray-600'
              }`}>{sec.label}</button>
          ))}
        </div>
        {/* Settings Content */}
        <div className="flex-1 overflow-y-auto p-3 md:p-5">
          <div className="space-y-4">
          {isLoading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-6 h-6 animate-spin text-brand-primary" />
              <span className="ml-2 text-sm text-brand-neutral">Loading...</span>
            </div>
          ) : (
            <>
              {/* Mechanics Section */}
              {activeSection === 'mechanics' && (
              <section className="bg-white rounded-2xl shadow-sm border border-brand-light/80 overflow-hidden">
                <div className="px-4 md:px-6 py-3 md:py-4 border-b border-brand-light/60 bg-gradient-to-r from-brand-light/40 to-white">
                  <div className="flex items-center gap-2">
                    <Wrench className="w-4 h-4 text-brand-primary" />
                    <h2 className="text-sm font-bold text-brand-dark">Main Job Settings — Mechanics</h2>
                  </div>
                  <p className="text-xs text-brand-neutral mt-0.5">
                    Select active AMC mechanics ·
                    {' '}<strong className="text-brand-dark">{selectedMechanicUids.size || 0}</strong> mechanic{selectedMechanicUids.size !== 1 ? 's' : ''} selected
                    {' '}= <strong className="text-brand-dark">{(selectedMechanicUids.size || 0) * 8}</strong> jobs/day capacity
                  </p>
                </div>
                <div className="p-4 md:p-6 space-y-3">
                  {/* Search + filter */}
                  <div className="flex items-center gap-2 flex-wrap">
                    <div className="relative flex-1 min-w-[200px]">
                      <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-brand-neutral" />
                      <input
                        type="text"
                        value={mechanicSearch}
                        onChange={(e) => setMechanicSearch(e.target.value)}
                        placeholder="Search by name or email..."
                        className="w-full pl-9 pr-3 py-2.5 border border-brand-light/80 rounded-xl text-sm bg-white focus:outline-none focus:ring-2 focus:ring-brand-primary/20 focus:border-brand-primary transition-all"
                      />
                    </div>
                    <div className="relative">
                      <Filter className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-brand-neutral pointer-events-none" />
                      <select
                        value={mechanicFilter}
                        onChange={(e) => setMechanicFilter(e.target.value)}
                        className="pl-9 pr-8 py-2.5 border border-brand-light/80 rounded-xl text-sm bg-white focus:outline-none focus:ring-2 focus:ring-brand-primary/20 focus:border-brand-primary appearance-none cursor-pointer transition-all"
                      >
                        <option value="all">All Designations</option>
                        {designations.map((d) => (
                          <option key={d} value={d}>{d}</option>
                        ))}
                      </select>
                    </div>
                  </div>

                  {/* Users list */}
                  {zuperUsers.length === 0 ? (
                    <p className="text-sm text-brand-neutral text-center py-4">No users cached. Click Sync Zuper to load.</p>
                  ) : (
                    <div className="space-y-1 max-h-[300px] overflow-y-auto">
                      {filteredMechanics.map((user) => {
                        const isSelected = selectedMechanicUids.has(user.user_uid);
                        return (
                          <label
                            key={user.user_uid}
                            className={`flex items-center gap-3 px-3 py-2.5 rounded-xl border cursor-pointer transition-all ${isSelected
                              ? 'border-brand-primary bg-brand-primary/5 shadow-sm shadow-brand-primary/10'
                              : 'border-brand-light/60 bg-white hover:border-brand-neutral/30 hover:shadow-sm'
                              }`}
                          >
                            <input
                              type="checkbox"
                              checked={isSelected}
                              onChange={() => handleMechanicToggle(user)}
                              className="w-4 h-4 rounded border-brand-light text-brand-primary focus:ring-brand-primary/20"
                            />
                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-medium text-brand-dark truncate">{user.name}</p>
                              <p className="text-[11px] text-brand-neutral truncate">
                                {[user.designation, user.role, user.email].filter(Boolean).join(' · ')}
                              </p>
                            </div>
                            {(user.designation || user.emp_code) && (
                              <span className="text-[11px] text-brand-neutral bg-brand-light px-2 py-0.5 rounded-full flex-shrink-0">{user.designation || user.emp_code}</span>
                            )}
                          </label>
                        );
                      })}
                    </div>
                  )}
                </div>
              </section>
              )}

              {/* Child Job Templates Section (categories + assigned users) */}
              {activeSection === 'child-jobs' && (
              <section className="bg-white rounded-2xl shadow-sm border border-brand-light/80 overflow-hidden">
                <div className="px-4 md:px-6 py-3 md:py-4 border-b border-brand-light/60 bg-gradient-to-r from-brand-light/40 to-white">
                  <div className="flex items-center gap-2">
                    <Tag className="w-4 h-4 text-brand-primary" />
                    <h2 className="text-sm font-bold text-brand-dark">Child Job Settings</h2>
                  </div>
                  <p className="text-xs text-brand-neutral mt-0.5">Set the Zuper category and assign team members for each child job type</p>
                </div>
                <div className="p-4 md:p-6 space-y-4">
                  {templates.map((tmpl, index) => (
                    <div key={index} className="border border-brand-light/60 rounded-xl p-4 space-y-3 hover:border-brand-light transition-all">
                      <div className="flex items-center gap-3 flex-wrap">
                        <span className="w-24 text-xs font-bold text-brand-dark bg-brand-light/40 px-2.5 py-1 rounded-lg">{tmpl.title}</span>
                        <select
                          value={tmpl.categoryUid || ''}
                          onChange={e => handleTemplateCategoryChange(index, e.target.value)}
                          className="flex-1 min-w-[200px] px-3 py-2 border border-brand-light/80 rounded-xl text-sm bg-white focus:outline-none focus:ring-2 focus:ring-brand-primary/20 focus:border-brand-primary appearance-none cursor-pointer transition-all"
                        >
                          <option value="">Same as parent job</option>
                          {categories.map(cat => (
                            <option key={cat.category_uid} value={cat.category_uid}>
                              {cat.category_name}{cat.custom ? ' *' : ''}
                            </option>
                          ))}
                        </select>
                      </div>
                      {/* Assigned users */}
                      <div className="flex items-start gap-2">
                        <Users className="w-4 h-4 text-brand-neutral mt-1 flex-shrink-0" />
                        <div className="flex-1 space-y-2">
                          <div className="flex items-center gap-1.5 flex-wrap">
                            {(tmpl.assignedUsers || []).map(u => (
                              <span key={u.userUid} className="inline-flex items-center gap-1 text-xs bg-brand-primary/10 text-brand-primary px-2 py-1 rounded-lg">
                                {u.name}
                                <button
                                  onClick={() => handleRemoveAssignedUser(index, u.userUid)}
                                  className="hover:text-red-500 transition-colors"
                                >
                                  <X className="w-3 h-3" />
                                </button>
                              </span>
                            ))}
                            {(tmpl.assignedUsers || []).length === 0 && (
                              <span className="text-xs text-brand-neutral italic">No one assigned (job created unassigned)</span>
                            )}
                          </div>
                          <select
                            value=""
                            onChange={e => { if (e.target.value) handleAddAssignedUser(index, e.target.value); }}
                            className="w-full px-3 py-2 border border-brand-light/80 rounded-xl text-sm bg-white focus:outline-none focus:ring-2 focus:ring-brand-primary/20 focus:border-brand-primary appearance-none cursor-pointer transition-all"
                          >
                            <option value="">Add team member...</option>
                            {zuperUsers
                              .filter(u => !(tmpl.assignedUsers || []).some(a => a.userUid === u.user_uid))
                              .map(user => (
                                <option key={user.user_uid} value={user.user_uid}>{user.name}</option>
                              ))
                            }
                          </select>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </section>
              )}

              {/* Schedule Matrix Section */}
              {activeSection === 'schedule' && (
              <section className="bg-white rounded-2xl shadow-sm border border-brand-light/80 overflow-hidden">
                <div className="px-4 md:px-6 py-3 md:py-4 border-b border-brand-light/60 bg-gradient-to-r from-brand-light/40 to-white">
                  <div className="flex items-center gap-2">
                    <Clock className="w-4 h-4 text-brand-primary" />
                    <h2 className="text-sm font-bold text-brand-dark">Daily Schedule Matrix</h2>
                  </div>
                  <p className="text-xs text-brand-neutral mt-0.5">Fixed 4+4 positions · Each defines times for Collection, AMC, Washing & Delivery stages</p>
                </div>

                <div className="p-4 md:p-6 space-y-8">
                  {/* Morning Shift */}
                  <div>
                    <div className="flex items-center gap-2.5 mb-4">
                      <div className="w-7 h-7 rounded-lg bg-amber-50 flex items-center justify-center">
                        <Sun className="w-4 h-4 text-amber-500" />
                      </div>
                      <div>
                        <h3 className="text-xs font-bold text-brand-dark">Morning Shift</h3>
                        <p className="text-[10px] text-brand-neutral">Positions 1–4</p>
                      </div>
                    </div>
                    <ScheduleTable
                      positions={morningPositions}
                      allPositions={schedule.positions}
                      onTimeChange={handlePositionTimeChange}
                    />
                  </div>

                  <hr className="border-brand-light/60" />

                  {/* Afternoon Shift */}
                  <div>
                    <div className="flex items-center gap-2.5 mb-4">
                      <div className="w-7 h-7 rounded-lg bg-indigo-50 flex items-center justify-center">
                        <Sunset className="w-4 h-4 text-indigo-500" />
                      </div>
                      <div>
                        <h3 className="text-xs font-bold text-brand-dark">Afternoon Shift</h3>
                        <p className="text-[10px] text-brand-neutral">Positions 5–8</p>
                      </div>
                    </div>
                    <ScheduleTable
                      positions={afternoonPositions}
                      allPositions={schedule.positions}
                      onTimeChange={handlePositionTimeChange}
                    />
                  </div>
                </div>
              </section>
              )}

              {/* Sticky save bar */}
              {hasChanged && (
                <div className="sticky bottom-0 bg-white border border-brand-light rounded-xl p-3 shadow-lg flex items-center justify-between">
                  <p className="text-sm text-brand-neutral">Unsaved changes</p>
                  <button
                    onClick={handleSave}
                    disabled={updateMutation.isPending}
                    className="flex items-center gap-1.5 px-5 py-2 text-sm font-medium text-white bg-gradient-to-r from-brand-primary to-brand-secondary rounded-xl hover:shadow-lg transition-all shadow-sm disabled:opacity-50"
                  >
                    {updateMutation.isPending ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <>
                        <Save className="w-4 h-4" />
                        Save Schedule
                      </>
                    )}
                  </button>
                </div>
              )}
            </>
          )}
          </div>
        </div>
      </main>
    </Layout>
  );
}

/**
 * Schedule table component - renders positions as card rows with stage columns
 */
function ScheduleTable({ positions, allPositions, onTimeChange }) {
  return (
    <div className="overflow-x-auto -mx-4 px-4">
      <div className="min-w-[640px]">
        {/* Column headers */}
        <div className="grid grid-cols-[3rem_1fr_1fr_1fr_1fr] gap-2 mb-2 px-1">
          <div className="text-[10px] font-semibold text-brand-neutral uppercase tracking-wider text-center">#</div>
          {STAGE_KEYS.map(key => {
            const color = STAGE_COLORS[key];
            return (
              <div key={key} className="flex items-center gap-1.5 px-2">
                <span className={`w-1.5 h-1.5 rounded-full ${color.dot}`} />
                <span className={`text-[10px] font-semibold uppercase tracking-wider ${color.text}`}>
                  {STAGE_LABELS[key]}
                </span>
              </div>
            );
          })}
        </div>

        {/* Position rows */}
        <div className="space-y-2">
          {positions.map(pos => {
            const globalIndex = allPositions.findIndex(p => p.position === pos.position);
            return (
              <div key={pos.position} className="grid grid-cols-[3rem_1fr_1fr_1fr_1fr] gap-2 items-center">
                {/* Position number */}
                <div className="flex justify-center">
                  <span className="w-8 h-8 flex items-center justify-center font-mono font-bold text-sm text-brand-primary bg-gradient-to-br from-brand-light to-white rounded-lg shadow-sm border border-brand-light/60">
                    {pos.position}
                  </span>
                </div>

                {/* Stage time cells */}
                {STAGE_KEYS.map(stageKey => {
                  const stage = pos[stageKey] || {};
                  const color = STAGE_COLORS[stageKey];
                  return (
                    <div key={stageKey} className={`rounded-xl border ${color.border} ${color.bg} px-2.5 py-2 transition-all hover:shadow-sm`}>
                      <div className="flex items-center gap-1.5">
                        <input
                          type="time"
                          value={stage.start || ''}
                          onChange={e => onTimeChange(globalIndex, stageKey, 'start', e.target.value)}
                          className="w-full px-1.5 py-1 border border-gray-200/80 rounded-lg text-[11px] font-medium bg-white/80 focus:outline-none focus:ring-2 focus:ring-brand-primary/20 focus:border-brand-primary/40 transition-all"
                        />
                        <span className="text-gray-300 text-[10px] font-medium flex-shrink-0">–</span>
                        <input
                          type="time"
                          value={stage.end || ''}
                          onChange={e => onTimeChange(globalIndex, stageKey, 'end', e.target.value)}
                          className="w-full px-1.5 py-1 border border-gray-200/80 rounded-lg text-[11px] font-medium bg-white/80 focus:outline-none focus:ring-2 focus:ring-brand-primary/20 focus:border-brand-primary/40 transition-all"
                        />
                      </div>
                    </div>
                  );
                })}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
